import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useCallback} from 'react';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {
  getQuotingApi,
  type CatalogueQuery,
  type AccessorySelectionAction,
  type EngineConnectionChoice,
  type EngineTurbosChoice,
  type QuoteOwnedChanges,
  type QuotePacket,
  type QuoteQueueItem,
  type MailboxWindow,
  type ManagementIntake,
} from '../api/quoting-api';
import {quotingKeys} from '../query-keys';
import {relayKeys} from '@/matic/features/relay/query-keys';
import {isApiError} from '@/matic/lib/api/error-types';

/**
 * These reads are deterministic: a refusal (422) or a missing request will not change
 * on retry. The engine being unreachable will — it answers 502 for a few seconds after
 * the app starts — so only gateway errors are retried, with React Query's backoff.
 */
const retryUnreachableDesk = (failures: number, error: Error) =>
  failures < 3 && isApiError(error) && [502, 503, 504].includes(error.status);

/** The worksheet is the engine's state, not Postgres: short staleness so a pin Otto
 *  made mid-stream shows up on the next refetch. */
const WORKSHEET_STALE_MS = 5000;

export function useManualMailboxIntake() {
  const token = useApiToken();
  const tenantId = useRequiredTenantId();
  const client = useQueryClient();
  const preview = useMutation({
    mutationFn: async (window: MailboxWindow) =>
      getQuotingApi(token).previewMailbox(window),
    retry: false,
  });
  const intake = useMutation({
    mutationFn: async ({window, ids}: {window: MailboxWindow; ids: string[]}) =>
      getQuotingApi(token).intakeMailbox(window, ids),
    retry: false,
    onSuccess() {
      // Show the committed result immediately; background list refreshes must
      // not delay confirmation or the first job-status read.
      void client.invalidateQueries({queryKey: quotingKeys.requests(tenantId)});
      void client.invalidateQueries({queryKey: relayKeys.all});
      void client.invalidateQueries({
        queryKey: quotingKeys.managementIntake(tenantId),
      });
    },
  });
  return {preview, intake};
}

export function useMailboxIntakeJobs(ids: string[], open: boolean) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.mailboxJobs(tenantId, ids),
    queryFn: async () => getQuotingApi(token).mailboxJobs(ids),
    enabled: open && ids.length > 0 && Boolean(token),
    retry: false,
    refetchInterval: (query) =>
      open &&
      query.state.data?.some((job) =>
        ['pending', 'reading', 'retry'].includes(job.status)
      )
        ? 3000
        : false,
  });
}

export function useManagementIntake() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.managementIntake(tenantId),
    queryFn: async () => getQuotingApi(token).managementIntake(),
    enabled: Boolean(token),
    // Fast while a read runs; otherwise a slow sweep so newly polled mail appears unprompted.
    refetchInterval: (query) =>
      query.state.data?.some((job) =>
        ['pending', 'reading', 'retry'].includes(job.status)
      )
        ? 3000
        : 60_000,
  });
}

/** The owner roster; config, so it only changes with a deploy. */
export function useQuoteOwners() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.owners(tenantId),
    queryFn: async () => getQuotingApi(token).quoteOwners(),
    enabled: Boolean(token),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useIntakeSettings() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.intakeSettings(tenantId),
    queryFn: async () => getQuotingApi(token).intakeSettings(),
    enabled: Boolean(token),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Whether this person may import past Inbox emails: admin/quality, and only while the Inbox
 * is not polled continuously (the server refuses the manual import then).
 */
export function useCanImportPastEmails() {
  const {userRole} = useAppContext();
  return ['admin', 'quality'].includes(userRole ?? '');
}

/** Start the full read of a request the keyword screen parked (`awaiting_read`). */
export function useReadIntakeRequest() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (job: ManagementIntake) =>
      getQuotingApi(token).promoteThread(job.thread_id),
    retry: false,
    onSuccess(result, job) {
      if (result.status === 'intake_disabled') return;
      // Resume polling immediately: the promoted version is queued under its own job ID.
      client.setQueryData<ManagementIntake[]>(
        quotingKeys.managementIntake(tenantId),
        (jobs) =>
          jobs?.map((row) =>
            row.id === job.id
              ? {...row, id: result.jobId, status: result.status, error: null}
              : row
          )
      );
      void client.invalidateQueries({
        queryKey: quotingKeys.managementIntake(tenantId),
      });
    },
    onError() {
      void client.invalidateQueries({
        queryKey: quotingKeys.managementIntake(tenantId),
      });
    },
  });
}

export function useQuoteRequests() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.requests(tenantId),
    queryFn: async () => getQuotingApi(token).listRequests(),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

export function useQuoteRequest(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.request(tenantId, id ?? ''),
    queryFn: async () => getQuotingApi(token).getRequest(id!),
    enabled: Boolean(token && id),
  });
}

export function useQuoteThread(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.thread(tenantId, id ?? ''),
    queryFn: async () => getQuotingApi(token).getThread(id!),
    enabled: Boolean(token && id),
    staleTime: 5 * 60_000,
  });
}

export function useWorksheetState(id: string | undefined, asOf: string) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.worksheet(tenantId, id ?? '', asOf),
    queryFn: async () => getQuotingApi(token).worksheetState(id!, asOf),
    enabled: Boolean(token && id),
    staleTime: WORKSHEET_STALE_MS,
  });
}

export function useQuoteFlow(id: string | undefined, asOf: string) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.flow(tenantId, id ?? '', asOf),
    queryFn: async () => getQuotingApi(token).flow(id!, asOf),
    enabled: Boolean(token && id),
    staleTime: WORKSHEET_STALE_MS,
  });
}

/** Field → stage, static for a data release; cached for the session. */
export function useWorksheetFieldStages() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.fields(tenantId),
    queryFn: async () => getQuotingApi(token).fieldStages(),
    enabled: Boolean(token),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useQuoteExports(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.exports(tenantId, id ?? ''),
    queryFn: async () => getQuotingApi(token).listExports(id!),
    enabled: Boolean(token && id),
    staleTime: WORKSHEET_STALE_MS,
  });
}

/** The request's MATIC record; refetched with the worksheet since writes refresh it. */
export function useQuoteRecord(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [...quotingKeys.request(tenantId, id ?? ''), 'record'],
    queryFn: async () => getQuotingApi(token).getRecord(id!),
    enabled: Boolean(token && id),
    staleTime: WORKSHEET_STALE_MS,
  });
}

/** The EEAA export plan (the header to confirm); fetched only while the form is open. */
export function useQuoteExportPlan(
  id: string | undefined,
  asOf: string,
  enabled: boolean
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [
      ...quotingKeys.worksheet(tenantId, id ?? '', asOf),
      'export-plan',
    ],
    queryFn: async () => getQuotingApi(token).exportPlan(id!, asOf),
    enabled: Boolean(token && id && enabled),
    staleTime: WORKSHEET_STALE_MS,
    retry: retryUnreachableDesk,
  });
}

/**
 * The packet the class tools should read: the worksheet's effective packet when a pin changed
 * what the engine quotes on (a chosen or inferred engine identity, a picked sound sheet), else
 * the request as read. One request had no engine key; read raw, the ladder came back
 * empty and the Sound row said the request "states no level", though it states 78 dB(A) at 25 ft.
 * `basis` keys the query on what the effective packet changes, so it refetches when that moves.
 */
const text = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

function ladderPacket(
  packet: QuotePacket | undefined,
  effective: Record<string, unknown> | undefined
): {body: Record<string, unknown> | undefined; basis: string} {
  if (!effective) return {body: packet?.raw, basis: 'request'};
  const lookup = (effective.lookup ?? {}) as Record<string, unknown>;
  const source = (effective.class_source ?? {}) as Record<string, unknown>;
  return {
    body: effective,
    basis: `${text(lookup.engine_key)}|${text(source.candidate_id)}`,
  };
}

/**
 * The request's class ladder, keyed with the worksheet's as-of so a pin that moves the
 * class (shape, insulation) refetches it alongside. While the Quote or Evidence view wants it.
 */
export function useQuoteClassLadder(
  packet: QuotePacket | undefined,
  asOf: string,
  enabled: boolean,
  effective?: Record<string, unknown>
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const {body, basis} = ladderPacket(packet, effective);
  return useQuery({
    queryKey: [...quotingKeys.ladder(tenantId, packet?.id ?? '', asOf), basis],
    queryFn: async () => getQuotingApi(token).requestClassLadder(body!),
    enabled: Boolean(token && packet && enabled),
    staleTime: WORKSHEET_STALE_MS,
    retry: retryUnreachableDesk,
  });
}

/** The class strip's evidence: per-class prices and the past requests near this ask. */
export function useQuoteClassEvidence(
  packet: QuotePacket | undefined,
  asOf: string,
  enabled: boolean,
  effective?: Record<string, unknown>
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const {body, basis} = ladderPacket(packet, effective);
  return useQuery({
    queryKey: [
      ...quotingKeys.classEvidence(tenantId, packet?.id ?? '', asOf),
      basis,
    ],
    queryFn: async () => getQuotingApi(token).requestClassEvidence(body!),
    enabled: Boolean(token && packet && enabled),
    staleTime: WORKSHEET_STALE_MS,
    retry: false,
  });
}

/** The spectrum the engine reads for an engine at a rating; static for a data release. */
export function useQuoteSound(
  engineKey: string | null | undefined,
  kw: number | null | undefined,
  enabled: boolean,
  asOf?: string
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.sound(tenantId, engineKey ?? '', kw ?? 0, asOf),
    queryFn: async () =>
      getQuotingApi(token).resolveSound(engineKey!, kw!, asOf),
    enabled: Boolean(token && engineKey && typeof kw === 'number' && enabled),
    staleTime: Number.POSITIVE_INFINITY,
    retry: retryUnreachableDesk,
  });
}

/** Past AES quotes by job key (identity and part number), keyed for lookup; static for a data release. */
export function useQuoteJobs(jobKeys: string[]) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQueries({
    queries: jobKeys.map((jobKey) => ({
      queryKey: quotingKeys.job(tenantId, jobKey),
      queryFn: async () => getQuotingApi(token).job(jobKey),
      enabled: Boolean(token),
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    })),
    combine: (results) =>
      new Map(
        results.flatMap((result) =>
          result.data ? [[result.data.jobKey, result.data] as const] : []
        )
      ),
  });
}

/** Every kW rating AES's sound data holds for an engine; static for a data release. */
/** The engine's data release, for the Pricing resource's footer. */
export function useEngineHealth() {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [...quotingKeys.all, 'health', tenantId],
    queryFn: async () => getQuotingApi(token).engineHealth(),
    enabled: Boolean(token),
    staleTime: 60_000,
    retry: retryUnreachableDesk,
  });
}

export function useEngineRatings(engineKey: string | null | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.engineRatings(tenantId, engineKey ?? ''),
    queryFn: async () => getQuotingApi(token).engineRatings(engineKey!),
    enabled: Boolean(token && engineKey),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** One attachment's bytes as a File for the viewer; immutable, so cached for the session. */
export function useQuoteAttachmentFile(
  id: string | undefined,
  name: string,
  enabled: boolean
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.attachment(tenantId, id ?? '', name),
    queryFn: async () => getQuotingApi(token).attachmentFile(id!, name),
    enabled: Boolean(token && id && name && enabled),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * A written workbook's customer quote page as a PDF, converted by the backend. A written
 * workbook never changes (the next export takes the next name), so it is fetched once.
 */
export function useQuoteExportPdf(id: string | undefined, name: string | null) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.exportPdf(tenantId, id ?? '', name ?? ''),
    async queryFn() {
      const blob = await getQuotingApi(token).getExportPdf(id!, name!);
      return new File([blob], name!.replace(/\.xlsx$/i, '.pdf'), {
        type: 'application/pdf',
      });
    },
    enabled: Boolean(token && id && name),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** Each part's outline print from the engine's index, one query per part number. */
export function useOutlinePrints(pns: string[]) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQueries({
    queries: pns.map((pn) => ({
      queryKey: quotingKeys.outlinePrint(tenantId, pn),
      queryFn: async () => getQuotingApi(token).outlinePrint(pn),
      enabled: Boolean(token),
      staleTime: Number.POSITIVE_INFINITY,
    })),
  });
}

/** A print's PDF from the server's staged folder; an error means it is not staged. */
export function useOutlinePrintPdf(file: string | null) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: quotingKeys.outlinePrintPdf(tenantId, file ?? ''),
    async queryFn() {
      const blob = await getQuotingApi(token).getOutlinePrintPdf(file!);
      return new File([blob], file!, {type: 'application/pdf'});
    },
    enabled: Boolean(token && file),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * Set a quote's progress, ERP quote number, owner, waiting-on or ETO level on its record. The queue row updates at once
 * (a toggle the user expects to stick); the list and the record refetch when it settles.
 * The worksheet is untouched: these fields are MATIC's, not the engine's.
 */
export function useUpdateQuoteRecord(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();
  const listKey = quotingKeys.requests(tenantId);
  return useMutation({
    async mutationFn(changes: QuoteOwnedChanges) {
      if (!id) throw new Error('No quote selected');
      await getQuotingApi(token).updateRecord(id, changes);
    },
    async onMutate(changes) {
      await queryClient.cancelQueries({queryKey: listKey});
      const previous = queryClient.getQueryData<QuoteQueueItem[]>(listKey);
      queryClient.setQueryData<QuoteQueueItem[]>(listKey, (rows) =>
        rows?.map((row) =>
          row.id === id
            ? {
                ...row,
                ...(changes.progress === undefined
                  ? {}
                  : {progress: changes.progress, progressSource: 'manual'}),
                ...(changes.erpQuoteNumber === undefined
                  ? {}
                  : {erpQuoteNumber: changes.erpQuoteNumber.trim() || null}),
                ...(changes.etoLevel === undefined
                  ? {}
                  : {etoLevel: changes.etoLevel || null}),
                ...(changes.assignee === undefined
                  ? {}
                  : {assignee: changes.assignee.trim() || null}),
                ...(changes.waitingOn === undefined
                  ? {}
                  : {waitingOn: changes.waitingOn || null}),
              }
            : row
        )
      );
      return {previous};
    },
    onError(_error, _changes, context) {
      if (context?.previous)
        queryClient.setQueryData(listKey, context.previous);
    },
    async onSettled() {
      if (!id) return;
      await Promise.all([
        queryClient.invalidateQueries({queryKey: listKey}),
        queryClient.invalidateQueries({
          queryKey: [...quotingKeys.request(tenantId, id), 'record'],
        }),
      ]);
    },
  });
}

/** Refetch everything the worksheet panel shows — after a person's write or an Otto turn. */
export function useInvalidateWorksheet(id: string | undefined) {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  return useCallback(() => {
    if (!id) return;
    void queryClient.invalidateQueries({
      queryKey: [...quotingKeys.all, 'worksheet', tenantId, id],
    });
    void queryClient.invalidateQueries({
      queryKey: [...quotingKeys.all, 'flow', tenantId, id],
    });
    void queryClient.invalidateQueries({
      queryKey: [...quotingKeys.all, 'ladder', tenantId, id],
    });
    void queryClient.invalidateQueries({
      queryKey: quotingKeys.exports(tenantId, id),
    });
    void queryClient.invalidateQueries({
      queryKey: [...quotingKeys.all, 'read-reviews', tenantId, id],
    });
    // The read step publishes into the queue: the packet and the list change too.
    void queryClient.invalidateQueries({
      queryKey: quotingKeys.request(tenantId, id),
    });
    void queryClient.invalidateQueries({
      queryKey: quotingKeys.requests(tenantId),
    });
  }, [queryClient, tenantId, id]);
}

/** The selected accessory evidence scope; fetching never changes a worksheet pin. */
export function useAccessoryPrecedent(
  id: string,
  args: Record<string, unknown>,
  enabled: boolean
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [
      ...quotingKeys.worksheet(tenantId, id, String(args.as_of)),
      'accessory-precedent',
      args,
    ],
    queryFn: async () => getQuotingApi(token).accessoryPrecedent(args),
    enabled: Boolean(token && enabled),
    staleTime: WORKSHEET_STALE_MS,
  });
}

/** Search filters only read evidence, isolated by tenant, request, date and all dimensions. */
export function useCatalogueParts(
  id: string,
  query: CatalogueQuery,
  asOf: string,
  enabled: boolean
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [
      ...quotingKeys.worksheet(tenantId, id, asOf),
      'catalogue-parts',
      query,
    ],
    queryFn: async () => getQuotingApi(token).catalogueParts(query, asOf),
    enabled: Boolean(token && enabled),
    retry: retryUnreachableDesk,
    staleTime: WORKSHEET_STALE_MS,
  });
}

/** Only fetch on opening a PN; retain tenant/request/date isolation in the cache. */
export function useQuotePartDetail(
  id: string | undefined,
  pn: string | null,
  asOf: string
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [...quotingKeys.request(tenantId, id ?? ''), 'part', pn, asOf],
    queryFn: async () => getQuotingApi(token).partLookup(pn!, asOf),
    enabled: Boolean(token && pn && asOf),
    staleTime: 5 * 60_000,
  });
}

export function useAccessorySelectionPreview(
  id: string | undefined,
  pn: string | null,
  asOf: string
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [
      ...quotingKeys.request(tenantId, id ?? ''),
      'accessory-selection',
      pn,
      asOf,
    ],
    queryFn: async () => getQuotingApi(token).accessoryOptions(id!, pn!, asOf),
    enabled: Boolean(token && id && pn && asOf),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSelectAccessory(id: string | undefined, asOf: string) {
  const token = useApiToken();
  const invalidate = useInvalidateWorksheet(id);
  return useMutation({
    async mutationFn(action: AccessorySelectionAction) {
      if (!id) throw new Error('Open a quote request before selecting a part.');
      return getQuotingApi(token).selectAccessory(id, asOf, action);
    },
    onSuccess: invalidate,
  });
}

export function useEngineConnectionChoices(
  id: string,
  asOf: string,
  open: boolean
) {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: [
      ...quotingKeys.request(tenantId, id),
      'engine-connection-options',
      asOf,
    ],
    queryFn: async () => getQuotingApi(token).engineConnectionOptions(id, asOf),
    enabled: Boolean(token && id && asOf && open),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSelectEngineTurbos(id: string, asOf: string) {
  const token = useApiToken();
  const tenantId = useRequiredTenantId();
  const client = useQueryClient();
  const invalidate = useInvalidateWorksheet(id);
  return useMutation({
    mutationFn: async (choice: EngineTurbosChoice) =>
      getQuotingApi(token).selectEngineTurbos(id, asOf, choice),
    onSuccess() {
      invalidate();
      void client.invalidateQueries({
        queryKey: [...quotingKeys.all, 'worksheet', tenantId],
      });
      void client.invalidateQueries({
        queryKey: [...quotingKeys.all, 'flow', tenantId],
      });
    },
  });
}

export function useSelectEngineConnection(id: string, asOf: string) {
  const token = useApiToken();
  const tenantId = useRequiredTenantId();
  const client = useQueryClient();
  const invalidate = useInvalidateWorksheet(id);
  return useMutation({
    mutationFn: async (choice: EngineConnectionChoice) =>
      getQuotingApi(token).selectEngineConnection(id, asOf, choice),
    onSuccess() {
      invalidate();
      void client.invalidateQueries({
        queryKey: [...quotingKeys.all, 'worksheet', tenantId],
      });
      void client.invalidateQueries({
        queryKey: [...quotingKeys.all, 'flow', tenantId],
      });
    },
  });
}
